import React from "react";
import { Box, Text } from "@chakra-ui/react";

export interface CreditCardProps {
  type?: "brand-dark" | "brand-light";
  company?: string;
  cardBrand?: string;
  cardNumber?: string;
  cardHolder?: string;
  cardExpiration?: string;
}

function getCardBrandKey(input: string): "visa" | "mastercard" | "amex" | "discover" | "other" {
  const value = input.toLowerCase().trim();
  if (value.includes("master")) return "mastercard";
  if (value.includes("visa")) return "visa";
  if (value.includes("amex") || value.includes("american express")) return "amex";
  if (value.includes("discover")) return "discover";
  return "other";
}

function CardBrandIcon({
  brand,
  lightMode,
}: {
  brand: "visa" | "mastercard" | "amex" | "discover" | "other";
  lightMode: boolean;
}) {
  const text = lightMode ? "#111827" : "#F8FAFC";

  if (brand === "mastercard") {
    return (
      <Box minW="76px" h="44px" display="flex" alignItems="center" justifyContent="flex-end">
        <Box as="svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" w="40px" h="40px" aria-label="Mastercard logo">
          <path fill="#ff5f00" d="M13.597 11.677H18.407V20.32H13.597z" />
          <path d="m13.902 15.999c0-1.68.779-3.283 2.092-4.322-2.382-1.878-5.849-1.466-7.727.932-1.863 2.382-1.451 5.833.947 7.712 2 1.573 4.795 1.573 6.795 0-1.329-1.038-2.107-2.642-2.107-4.322Z" fill="#eb001b" />
          <path d="m24.897 15.999c0 3.039-2.459 5.497-5.497 5.497-1.237 0-2.428-.412-3.39-1.176 2.382-1.878 2.795-5.329.916-7.727-.275-.336-.58-.657-.916-.916 2.382-1.878 5.849-1.466 7.712.932.764.962 1.176 2.153 1.176 3.39Z" fill="#f79e1b" />
        </Box>
      </Box>
    );
  }
  if (brand === "visa") {
    return (
      <Box minW="76px" h="44px" display="flex" alignItems="center" justifyContent="flex-end">
        {lightMode ? (
          <Box as="svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" w="40px" h="40px" aria-label="Visa logo">
            <path d="m13.392 12.624-2.838 6.77h-1.851l-1.397-5.403c-.085-.332-.158-.454-.416-.595-.421-.229-1.117-.443-1.728-.576l.041-.196h2.98c.38 0 .721.253.808.69l.738 3.918 1.822-4.608h1.84Z" fill="#1434cb" />
            <path d="m20.646 17.183c.008-1.787-2.47-1.886-2.453-2.684.005-.243.237-.501.743-.567.251-.032.943-.058 1.727.303l.307-1.436c-.421-.152-.964-.299-1.638-.299-1.732 0-2.95.92-2.959 2.238-.011.975.87 1.518 1.533 1.843.683.332.912.545.909.841-.005.454-.545.655-1.047.663-.881.014-1.392-.238-1.799-.428l-.318 1.484c.41.188 1.165.351 1.947.359 1.841 0 3.044-.909 3.05-2.317" fill="#1434cb" />
            <path d="m25.423 12.624h-1.494c-.337 0-.62.195-.746.496l-2.628 6.274h1.839l.365-1.011h2.247l.212 1.011h1.62l-1.415-6.77Zm-2.16 4.372.922-2.542.53 2.542h-1.452Z" fill="#1434cb" />
            <path fill="#1434cb" d="M15.894 12.624 14.446 19.394 12.695 19.394 14.143 12.624 15.894 12.624z" />
          </Box>
        ) : (
          <Box as="svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" w="40px" h="40px" aria-label="Visa logo">
            <rect x="2" y="7" width="28" height="18" rx="3" ry="3" fill="#1434cb" />
            <path d="m13.392 12.624-2.838 6.77h-1.851l-1.397-5.403c-.085-.332-.158-.454-.416-.595-.421-.229-1.117-.443-1.728-.576l.041-.196h2.98c.38 0 .721.253.808.69l.738 3.918 1.822-4.608h1.84Z" fill="#fff" />
            <path d="m20.646 17.183c.008-1.787-2.47-1.886-2.453-2.684.005-.243.237-.501.743-.567.251-.032.943-.058 1.727.303l.307-1.436c-.421-.152-.964-.299-1.638-.299-1.732 0-2.95.92-2.959 2.238-.011.975.87 1.518 1.533 1.843.683.332.912.545.909.841-.005.454-.545.655-1.047.663-.881.014-1.392-.238-1.799-.428l-.318 1.484c.41.188 1.165.351 1.947.359 1.841 0 3.044-.909 3.05-2.317" fill="#fff" />
            <path d="m25.423 12.624h-1.494c-.337 0-.62.195-.746.496l-2.628 6.274h1.839l.365-1.011h2.247l.212 1.011h1.62l-1.415-6.77Zm-2.16 4.372.922-2.542.53 2.542h-1.452Z" fill="#fff" />
            <path fill="#fff" d="M15.894 12.624 14.446 19.394 12.695 19.394 14.143 12.624 15.894 12.624z" />
          </Box>
        )}
      </Box>
    );
  }
  if (brand === "amex") {
    return (
      <Box minW="76px" h="44px" display="flex" alignItems="center" justifyContent="flex-end">
        <Box as="svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" w="40px" h="40px" aria-label="American Express logo">
          <rect x="2" y="7" width="28" height="18" rx="3" ry="3" fill="#0f70ce" />
          <path d="m17.679 14.433h2.61l.502 1.148h1.78l-2.531-5.754h-2.039l-2.531 5.754h1.734l.477-1.148Zm1.307-3.135.775 1.844h-1.535l.761-1.844Z" fill="#fff" />
          <path d="M22.542 9.827H25.018L26.302 13.39 27.604 9.827H30V15.581H28.45V11.603L26.977 15.581H25.608L24.124 11.631V15.581H22.542V9.827z" fill="#fff" />
        </Box>
      </Box>
    );
  }
  if (brand === "discover") {
    return (
      <Box minW="76px" h="44px" display="flex" alignItems="center" justifyContent="flex-end">
        <Box as="svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" w="40px" h="40px" aria-label="Discover logo">
          <rect x="2" y="7" width="28" height="18" rx="3" ry="3" fill="#fff" />
          <path d="m27 7h-8c4.971 0 9 4.029 9 9s-4.029 9-9 9h8c1.657 0 3-1.343 3-3v-12c0-1.657-1.343-3-3-3Z" fill="#f47922" />
          <path d="m18.461 16c0 1.105-.895 2-2 2s-2-.895-2-2 .895-2 2-2 2 .895 2 2Z" fill="#f47922" />
        </Box>
      </Box>
    );
  }
  return (
    <Box
      minW="76px"
      h="44px"
      display="flex"
      alignItems="center"
      justifyContent="flex-end"
    >
      <Text fontSize="11px" fontWeight="700" letterSpacing="0.04em" color={text}>
        CARD
      </Text>
    </Box>
  );
}

const CreditCard: React.FC<CreditCardProps> = ({
  type = "brand-dark",
  company = "Untitled.",
  cardBrand,
  cardNumber = "1234 1234 1234 1234",
  cardHolder = "OLIVIA RHYE",
  cardExpiration = "06/28",
}) => {
  // Use Tessera's primary brand gradient family for billing card backgrounds.
  const darkGradient = "linear-gradient(135deg, #38BBD1 0%, #3EEAEC 100%)";
  const lightGradient = "linear-gradient(135deg, #AEEAF3 0%, #D8F7FB 100%)";
  const textColor = type === "brand-light" ? "#0B2239" : "#F8FAFC";
  const dimColor = type === "brand-light" ? "rgba(11,34,57,0.7)" : "rgba(248,250,252,0.75)";
  const brandColor = "#FFFFFF";
  const brandKey = getCardBrandKey(cardBrand || company);

  return (
    <Box
      borderRadius="16px"
      p={5}
      minH="210px"
      bg={type === "brand-light" ? lightGradient : darkGradient}
      border="1px solid"
      borderColor={type === "brand-light" ? "rgba(59,130,246,0.2)" : "rgba(147,197,253,0.25)"}
      boxShadow="0 10px 24px rgba(2,6,23,0.25)"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Text fontSize="sm" fontWeight="600" color={textColor}>
          {company}
        </Text>
        <Box mt="-6px" mr="-4px">
          <CardBrandIcon brand={brandKey} lightMode={type === "brand-light"} />
        </Box>
      </Box>

      <Text fontSize={{ base: "lg", md: "xl" }} letterSpacing="0.08em" fontWeight="700" color={textColor}>
        {cardNumber}
      </Text>

      <Box display="flex" justifyContent="space-between" alignItems="flex-end">
        <Box>
          <Text fontSize="10px" textTransform="uppercase" letterSpacing="0.08em" color={dimColor}>
            Card holder
          </Text>
          <Text fontSize="sm" fontWeight="600" color={textColor}>
            {cardHolder}
          </Text>
        </Box>
        <Box textAlign="right">
          <Text fontSize="10px" textTransform="uppercase" letterSpacing="0.08em" color={dimColor}>
            Expires
          </Text>
          <Text fontSize="sm" fontWeight="600" color={brandColor}>
            {cardExpiration}
          </Text>
        </Box>
      </Box>
    </Box>
  );
};

export default CreditCard;

